// ═══════════════════════════════════════════════════════════════
// V·IANet Governance — Puesto Detail Page
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useI18n } from '../../../../../i18n';
import { useActiveDocument } from '../../../../../context/ActiveDocumentContext';
import { AppHeader } from '../../../../shared/components/AppHeader';
import { LoadingSpinner } from '../../../../shared/components/LoadingSpinner';
import {
  fetchPuesto,
  fetchCadenaMando,
  fetchSubordinados,
  crearFichaPuesto,
} from '../../../../shared/services/api';
import { PersonaViewerModal } from '../../components/PersonaViewerModal';
import { NIVEL_FALLBACK } from '../../../../../lib/nivel-style';
// 0615i: useHasNivelModulo eliminado - tab Puestos es solo lectura,
// edicion via tab Administracion del modulo M1.

const GOB_COLORS: Record<string, string> = {
  DPO: '#1D4ED8',
  OFICIAL_CUMPLIMIENTO: '#16A34A',
  PROPIETARIO_RIESGO: '#DC2626',
  AUDITOR_INTERNO: '#7C3AED',
  RESPONSABLE_CALIDAD: '#C2410C',
  PROPIETARIO_PROCESO: '#33BCE1',
  MIEMBRO_CONSEJO: '#B45309',
  SECRETARIO_CONSEJO: '#D97706',
  PERSONALIZADO: '#6B7280',
};

// ─── Styles ───
const cardStyle: React.CSSProperties = {
  background: '#fff',
  border: '1px solid #E2E6ED',
  borderRadius: 10,
  padding: 20,
};

const labelStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  color: '#5A6171',
  marginBottom: 4,
  display: 'block',
};

const valueStyle: React.CSSProperties = {
  fontSize: 14,
  color: '#1A1D23',
  marginBottom: 12,
};

const linkStyle: React.CSSProperties = {
  color: '#2F5496',
  textDecoration: 'none',
  cursor: 'pointer',
  fontWeight: 500,
};

const btnPrimary: React.CSSProperties = {
  padding: '8px 16px',
  fontSize: 13,
  fontWeight: 600,
  color: '#fff',
  background: '#2F5496',
  border: 'none',
  borderRadius: 8,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

const btnSecondary: React.CSSProperties = {
  ...btnPrimary,
  background: 'transparent',
  color: '#2F5496',
  border: '1px solid #2F5496',
};

// ─── Types ───
interface CadenaMandoItem {
  id: string;
  titulo: string;
  codigo: string | null;
  nivel: string;
  persona?: { nombre: string; apellidos: string } | null;
}

interface SubordinadoItem {
  id: string;
  titulo: string;
  codigo: string | null;
  nivel: string;
  persona?: { nombre: string; apellidos: string } | null;
}

interface RolGobernanza {
  tipoRol: string;
  nombrePersonalizado?: string;
}

// ═══════════════════════════════════════════════════════════════
// Component
// ═══════════════════════════════════════════════════════════════
export function PuestoDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useI18n();

  // 0615i ADR-024: detalle puesto es solo lectura. La edicion del puesto
  // (titulo, codigo, nivel, ficha, asignaciones) vive en tab Administracion.

  const [puesto, setPuesto] = useState<any>(null);
  const [cadena, setCadena] = useState<CadenaMandoItem[]>([]);
  const [subordinados, setSubordinados] = useState<SubordinadoItem[]>([]);
  const [totalIndirectos, setTotalIndirectos] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [creatingFicha, setCreatingFicha] = useState(false);
  // 0615k: viewer-only modal de persona (link a la persona asignada del puesto).
  const [viewerPersonaId, setViewerPersonaId] = useState<string | null>(null);

  const { setActivePuesto, puestoId: currentPuestoId } = useActiveDocument();
  useEffect(() => {
    if (id && puesto?.titulo) {
      setActivePuesto(id, puesto.titulo);
    }
    return () => {
      if (currentPuestoId === id) setActivePuesto(null);
    };
  }, [id, puesto?.titulo, currentPuestoId, setActivePuesto]);

  const loadData = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError('');
    try {
      const [puestoRes, cadenaRes, subRes] = await Promise.all([
        fetchPuesto(id),
        fetchCadenaMando(id),
        fetchSubordinados(id),
      ]);
      setPuesto(puestoRes.data);
      setCadena((cadenaRes.data ?? []) as unknown as CadenaMandoItem[]);
      const rawSub = subRes.data;
      const subArr = Array.isArray(rawSub)
        ? rawSub
        : ((rawSub as { hijos?: unknown[]; subordinados?: unknown[] })?.hijos ??
          (rawSub as { subordinados?: unknown[] })?.subordinados ??
          []);
      const allSubs = Array.isArray(subArr) ? subArr : [];
      const directSubs = allSubs.filter((item: any) => !item.profundidad || item.profundidad === 1);
      const indirectCount = allSubs.filter(
        (item: any) => item.profundidad && item.profundidad > 1,
      ).length;
      setTotalIndirectos(indirectCount);
      setSubordinados(
        directSubs
          .map((item: unknown) => (item as { puesto?: unknown }).puesto ?? item)
          .filter(Boolean) as unknown as SubordinadoItem[],
      );
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Error';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // 0615i: handleStartEdit + handleSaveEdit eliminados - edicion en tab Administracion.

  const handleCrearFicha = async () => {
    if (!id) return;
    setCreatingFicha(true);
    try {
      await crearFichaPuesto(id);
      await loadData();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Error';
      setError(msg);
    } finally {
      setCreatingFicha(false);
    }
  };

  // ─── Render states ───
  if (loading) {
    return (
      <div style={{ padding: 32 }}>
        <LoadingSpinner />
        <p style={{ textAlign: 'center', color: '#5A6171', marginTop: 12 }}>
          {t.structure.puestoDetail?.loading ?? 'Cargando puesto...'}
        </p>
      </div>
    );
  }

  if (error || !puesto) {
    return (
      <div style={{ padding: 32, textAlign: 'center' }}>
        <p style={{ color: '#DC2626', fontSize: 14 }}>
          {error || (t.structure.puestoDetail?.notFound ?? 'Puesto no encontrado')}
        </p>
        <button style={btnSecondary} onClick={() => navigate(-1)}>
          {t.structure.puestoDetail?.goBack ?? 'Volver'}
        </button>
      </div>
    );
  }

  // ─── Data extraction ───
  const npConf = puesto.nivelPuesto as
    | { nombre?: string; color?: string | null; colorFondo?: string | null }
    | undefined;
  const nivelStyle = npConf?.color
    ? { color: npConf.color, bg: npConf.colorFondo ?? NIVEL_FALLBACK.bg }
    : NIVEL_FALLBACK;
  const nivelLabel = npConf?.nombre ?? '—';
  const asignacionActiva = puesto.asignaciones?.find((a: any) => a.activo);
  const personaAsignada = asignacionActiva?.persona ?? puesto.persona ?? null;
  const personaId = personaAsignada?.id ?? null;
  const personaName = personaAsignada
    ? `${personaAsignada.nombre} ${personaAsignada.apellidos}`
    : null;
  const roles: RolGobernanza[] = puesto.rolesGobernanza ?? [];
  const fichaDoc = puesto.fichaDocumento ?? null;
  const drawerT = t.structure?.drawer ?? ({} as any);
  const detailT = (t.structure as any)?.puestoDetail ?? ({} as any);

  return (
    <div>
      <AppHeader modulo="Structure" titulo={puesto.titulo} />

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 2fr',
          gap: 24,
          padding: '24px 32px',
          maxWidth: 1400,
        }}
      >
        {/* ─── LEFT PANEL: Position details ─── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={cardStyle}>
            {/* Title */}
            <h2
              style={{
                fontSize: 20,
                fontWeight: 700,
                color: '#1F3864',
                margin: '0 0 4px 0',
              }}
            >
              {puesto.titulo}
            </h2>

            {/* Code */}
            {puesto.codigo && (
              <p
                style={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 12,
                  color: '#5A6171',
                  margin: '0 0 12px 0',
                }}
              >
                {puesto.codigo}
              </p>
            )}

            {/* Level badge */}
            <div style={{ marginBottom: 16 }}>
              <span style={labelStyle}>{drawerT.nivel}</span>
              <span
                style={{
                  display: 'inline-block',
                  padding: '3px 10px',
                  borderRadius: 6,
                  fontSize: 12,
                  fontWeight: 600,
                  color: nivelStyle.color,
                  background: nivelStyle.bg,
                  border: `1px solid ${nivelStyle.color}30`,
                }}
              >
                {nivelLabel}
              </span>
            </div>

            {/* Department */}
            <div style={{ marginBottom: 12 }}>
              <span style={labelStyle}>{drawerT.departamento}</span>
              <p style={valueStyle}>{puesto.departamento?.nombre ?? '—'}</p>
            </div>

            {/* Organization */}
            <div style={{ marginBottom: 12 }}>
              <span style={labelStyle}>{drawerT.organizacion}</span>
              <p style={valueStyle}>
                {puesto.organizacion?.nombre ?? puesto.departamento?.organizacion?.nombre ?? '—'}
              </p>
            </div>

            {/* Persona assigned */}
            <div style={{ marginBottom: 12 }}>
              <span style={labelStyle}>{drawerT.assignedPerson}</span>
              {personaId && personaName ? (
                <p
                  style={{ ...valueStyle, ...linkStyle }}
                  // 0615k: viewer-only modal de persona en lugar de navegar a admin.
                  onClick={() => setViewerPersonaId(personaId)}
                >
                  {personaName}
                </p>
              ) : (
                <p style={{ ...valueStyle, color: '#DC2626', fontStyle: 'italic' }}>
                  {drawerT.vacant}
                </p>
              )}
            </div>

            {/* Governance roles */}
            {roles.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <span style={labelStyle}>{drawerT.governanceRoles}</span>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
                  {roles.map((rol, i) => {
                    const tipo = rol.tipoRol;
                    const color = GOB_COLORS[tipo] ?? '#6B7280';
                    const label = rol.nombrePersonalizado ?? tipo.replace(/_/g, ' ');
                    return (
                      <span
                        key={i}
                        style={{
                          display: 'inline-block',
                          padding: '2px 8px',
                          borderRadius: 12,
                          fontSize: 11,
                          fontWeight: 600,
                          color: '#fff',
                          background: color,
                        }}
                      >
                        {label}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Cadena de mando */}
          <div style={cardStyle}>
            <h3 style={{ fontSize: 14, fontWeight: 600, color: '#1F3864', margin: '0 0 10px 0' }}>
              {drawerT.chainOfCommand}
            </h3>
            {cadena.length === 0 ? (
              <p style={{ fontSize: 13, color: '#9CA3AF', fontStyle: 'italic' }}>—</p>
            ) : (
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {cadena.map((item) => (
                  <li
                    key={item.id}
                    style={{
                      padding: '6px 0',
                      borderBottom: '1px solid #F3F4F6',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                    <span
                      style={{
                        ...linkStyle,
                        fontSize: 13,
                      }}
                      onClick={() => navigate(`/structure/puestos/${item.id}`)}
                    >
                      {item.titulo}
                    </span>
                    <span
                      style={{
                        fontSize: 10,
                        color: NIVEL_FALLBACK.color,
                        fontWeight: 600,
                      }}
                    >
                      {item.nivel ?? '—'}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Subordinados */}
          <div style={cardStyle}>
            <h3 style={{ fontSize: 14, fontWeight: 600, color: '#1F3864', margin: '0 0 10px 0' }}>
              {drawerT.subordinates}
              {(subordinados.length > 0 || totalIndirectos > 0) && (
                <span style={{ fontWeight: 400, fontSize: 12, color: '#5A6171', marginLeft: 8 }}>
                  {subordinados.length} directos
                  {totalIndirectos > 0 ? ` · ${totalIndirectos} indirectos` : ''}
                </span>
              )}
            </h3>
            {subordinados.length === 0 ? (
              <p style={{ fontSize: 13, color: '#9CA3AF', fontStyle: 'italic' }}>—</p>
            ) : (
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {subordinados.map((item) => (
                  <li
                    key={item.id}
                    style={{
                      padding: '6px 0',
                      borderBottom: '1px solid #F3F4F6',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                    <span
                      style={{
                        ...linkStyle,
                        fontSize: 13,
                      }}
                      onClick={() => navigate(`/structure/puestos/${item.id}`)}
                    >
                      {item.titulo}
                    </span>
                    {item.persona && (
                      <span style={{ fontSize: 11, color: '#6B7280' }}>
                        ({item.persona.nombre} {item.persona.apellidos})
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* 0615i: bloque "editing" + boton "Editar" eliminados.
              Edicion del puesto vive en tab Administracion del modulo. */}
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              style={btnPrimary}
              onClick={() => navigate(`/structure?focusPuesto=${id}&tab=organigrama`)}
            >
              {detailT?.viewInOrgChart ?? t.structure.orgChart.viewInOrgChart}
            </button>
          </div>
        </div>

        {/* ─── RIGHT PANEL: Ficha ProseMirror ─── */}
        <div style={cardStyle}>
          {fichaDoc ? (
            <div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  marginBottom: 16,
                }}
              >
                <h3 style={{ fontSize: 16, fontWeight: 600, color: '#1F3864', margin: 0 }}>
                  {fichaDoc.titulo ?? fichaDoc.nombre ?? detailT?.fichaTitle ?? 'Ficha de puesto'}
                </h3>
                {fichaDoc.estado && (
                  <span
                    style={{
                      display: 'inline-block',
                      padding: '2px 8px',
                      borderRadius: 12,
                      fontSize: 11,
                      fontWeight: 600,
                      color: fichaDoc.estado === 'PUBLICADO' ? '#059669' : '#D97706',
                      background: fichaDoc.estado === 'PUBLICADO' ? '#ECFDF5' : '#FFFBEB',
                    }}
                  >
                    {fichaDoc.estado === 'PUBLICADO' ? 'Vigente' : fichaDoc.estado}
                  </span>
                )}
              </div>
              {fichaDoc.codigo && (
                <p
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 12,
                    color: '#5A6171',
                    margin: '0 0 16px 0',
                  }}
                >
                  {fichaDoc.codigo}
                </p>
              )}
              <button style={btnPrimary} onClick={() => navigate(`/documento/${fichaDoc.codigo}`)}>
                {detailT?.openDocument ?? 'Abrir documento'}
              </button>
            </div>
          ) : (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 300,
                gap: 16,
              }}
            >
              <svg
                width={48}
                height={48}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={1}
                strokeLinecap="round"
                style={{ opacity: 0.3 }}
              >
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
              </svg>
              <p style={{ fontSize: 14, color: '#5A6171', textAlign: 'center' }}>
                {detailT?.noFicha ?? 'Este puesto no tiene ficha de caracterización asociada.'}
              </p>
              <button style={btnPrimary} onClick={handleCrearFicha} disabled={creatingFicha}>
                {creatingFicha
                  ? (detailT?.creatingFicha ?? 'Creando...')
                  : (detailT?.createFicha ?? 'Crear ficha de puesto')}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* 0615k: viewer-only modal de persona asignada al puesto. */}
      <PersonaViewerModal personaId={viewerPersonaId} onClose={() => setViewerPersonaId(null)} />
    </div>
  );
}
